import {useFormik} from 'formik';
import * as Yup from 'yup';

import {Button, Input} from '@/components';
import Modal from '@/components/Modals';
import type {BusinessAdminInviteRequest} from '@/types';

interface CreateInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: BusinessAdminInviteRequest) => Promise<void>;
  isLoading: boolean;
}

const validationSchema = Yup.object({
  fullName: Yup.string().required('Full name is required'),
  businessName: Yup.string().required('Business name is required'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Business email is required'),
});

type FormValues = BusinessAdminInviteRequest;

const CreateInviteModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}: CreateInviteModalProps) => {
  const formik = useFormik<FormValues>({
    initialValues: {
      fullName: '',
      businessName: '',
      email: '',
    },
    validationSchema,
    onSubmit: async (values, {resetForm}) => {
      await onSubmit(values);
      resetForm();
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const fieldError = (field: keyof FormValues) =>
    formik.touched[field] && formik.errors[field] ? (
      <p className="text-red-500 text-xs -mt-2">{formik.errors[field]}</p>
    ) : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      width="w-11/12 sm:w-5/12 md:w-5/12 lg:w-4/12 xl:w-3/12 px-4 sm:px-0"
      title="Create Invite"
      closeOnOutsideClick
    >
      <div className="pt-3 px-2">
        <form onSubmit={formik.handleSubmit} className="flex flex-col gap-3">
          <Input
            label="Full Name"
            placeholder="Contact's full name"
            value={formik.values.fullName}
            onChange={formik.handleChange('fullName')}
            onBlur={formik.handleBlur('fullName')}
            className="w-full"
          />
          {fieldError('fullName')}

          <Input
            label="Business Name"
            placeholder="Registered business name"
            value={formik.values.businessName}
            onChange={formik.handleChange('businessName')}
            onBlur={formik.handleBlur('businessName')}
            className="w-full"
          />
          {fieldError('businessName')}

          <Input
            label="Email"
            placeholder="yourname@example.com"
            type="email"
            value={formik.values.email}
            onChange={formik.handleChange('email')}
            onBlur={formik.handleBlur('email')}
            className="w-full"
          />
          {fieldError('email')}

          <div className="text-end pb-5 mt-2">
            <Button
              type="submit"
              variant="solid"
              colorScheme="cyan"
              disabled={!formik.isValid || !formik.dirty || isLoading}
              isLoading={isLoading}
              loadingText="Creating..."
            >
              Create Invite
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default CreateInviteModal;
